import { OfficesService } from '@/services/api/franchises.service';
import { PlansService } from '@/services/api/plans.service';
import { effect, inject, Injectable, signal } from '@angular/core';
import { Plan, PlanGroup } from 'src/app/models/plan.model';

@Injectable({
  providedIn: 'root'
})
export class PlanFilterService {

  plansService = inject(PlansService);
  officeService = inject(OfficesService);

  includeArchived = signal<boolean>(false);
  planGroups = signal<PlanGroup[]>([]);
  filteredPlanGroups = signal<PlanGroup[]>([]);
  distinctCollections = signal<string[]>([]);
  selectedCollectionString = signal<string | null>('All');
  searchQuery = signal<string>('');
  dialogOpen = signal<boolean>(false);

  constructor() { 
    effect(() => {
      const plans = this.plansService.allPlansData();
      const selectedOffice = this.officeService.selectedOffice();
      if(selectedOffice && plans.length > 0) {
        console.log('grouping plans',plans,selectedOffice.FID);
        this.groupPlans(plans,selectedOffice.FID);
      }
     });
     this.plansService.populatePlans();
  }

  groupPlans(plans: Plan[],fid: number) {
    console.log('grouping plans',plans,fid);
    const allPlans = plans.filter(plan => plan.PlanID !== 1 && plan.PlanPrices?.some(p => p.FID === fid))
      .map(plan => ({
        ...plan,
        PlanPrices: plan.PlanPrices?.filter(p => p.FID === fid)
      }));
    const grouped = allPlans.reduce((acc, plan) => {
      const family = plan.Family;
  
      if (!acc[family]) {
        acc[family] = { Family: family, Plans: [] };
      }
  
      acc[family].Plans.push(plan);
      return acc;
    }, {} as Record<string, PlanGroup>);
  
    const planGroups = Object.values(grouped);
    this.planGroups.set(planGroups);
    this.applyFilters(); // Apply initial filters
  }

  setDialogOpen(open: boolean) {
    console.log('setDialogOpen:', open);
    this.dialogOpen.set(open);
  }

  applyFilters() {
    const search = this.searchQuery().toLowerCase();
    const includeArchived = this.includeArchived();

    // Apply archive and search filters first
    const partiallyFiltered = this.planGroups().map(group => {
        const filteredPlans = group.Plans.filter(plan => {
            const matchesSearch = plan.PlanName.toLowerCase().includes(search);
            const matchesArchived = includeArchived ? true : !plan.Archived;
            return matchesSearch && matchesArchived;
        });

        return { ...group, Plans: filteredPlans };
    }).filter(group => group.Plans.length > 0);

    // Sort the groups alphabetically by Family
    partiallyFiltered.sort((a, b) => a.Family.localeCompare(b.Family));

    // Extract collections from the partially filtered plans
    const allPlans = this.extractPlans(partiallyFiltered);
    this.extractAndSetCollections(allPlans);

    // Default to 'All' collections if a search query is active
    if (search) {
        this.selectedCollectionString.set('All');
    }
    const collection = this.selectedCollectionString();

    // Apply the collection filter
    const fullyFiltered = partiallyFiltered.map(group => {
        const filteredPlans = group.Plans.filter(plan => {
            const matchesCollection = collection === 'All' || plan.Collection === collection;
            return matchesCollection;
        });

        return { ...group, Plans: filteredPlans };
    }).filter(group => group.Plans.length > 0);

    // Sort the fully filtered groups alphabetically by Family
    fullyFiltered.sort((a, b) => a.Family.localeCompare(b.Family));

    this.filteredPlanGroups.set(fullyFiltered);
}

  extractAndSetCollections(plans: Plan[]) {
        const collections = Array.from(new Set(plans.map(plan => plan.Collection)));
        this.distinctCollections.set(['All', ...collections]);
}

  extractPlans(groups: PlanGroup[]) {
    return groups.map(group => group.Plans).flat();
  }

  onSelectCollection(collection: string) {
    this.selectedCollectionString.set(collection);
    this.applyFilters();
  }

  setIncludeArchived(include: boolean,) {
    this.includeArchived.set(include);
    this.applyFilters();
  }

  searchPlans(query: string) {
    this.searchQuery.set(query);
    this.applyFilters();
  }
}